/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/settings/advanced/performance/_client.tsx` (verbatim,
 * minus the inert `"use client"`). Zero `next/*` edges, zero server sinks, and
 * all three of its ops (`perf.manifest`, `perf.history`, `perf.run`) are already
 * mounted on the Hono host.
 *
 * That combination is why THIS component is the slice's live-render proof: it
 * fetches its own data, so mounting it proves the whole chain — browser → Vite
 * bundle → Hono host → contract → core → disk — for ported shared code, not just
 * that the bundle compiles. See task #44398's evidence for the run.
 *
 * Reached by 22 root pages across 7 sections in the Next tree (through the
 * settings shell), the widest span in the shared set after `_modes`.
 */

import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
  FormField,
  Input,
  Switch,
  formatDateTime,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  perf,
  type PerfHistoryEntry,
  type PerfResult,
} from "@excellent/ui/api/contracts/bench";

interface RunSummary {
  started_at: string;
  finished_at: string;
  base_url: string;
  iterations: number;
  warmup: number;
  total: number;
  ok: number;
  overall_p50_ms: number;
  overall_p95_ms: number;
  use_etag: boolean;
  results: PerfResult[];
}

function fmt(n: number) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 100) return n.toFixed(0);
  if (n >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

function bytesFmt(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

interface PerformancePanelProps {
  /** Hide the "Audit" card title — the parent surface owns the page header.
   *  The endpoint count subtitle + Run button still render. */
  hideAuditTitle?: boolean;
}

export function PerformancePanel({ hideAuditTitle = false }: PerformancePanelProps = {}) {
  const { data: manifestEnvelope, isLoading: manifestLoading } = useApi(
    perf.manifest,
    {},
    { refreshInterval: 0 },
  );
  const { data: historyEnvelope, mutate: refetchHistory } = useApi(
    perf.history,
    {},
    { refreshInterval: 0 },
  );
  const manifest = manifestEnvelope;
  const history = historyEnvelope?.runs ?? [];

  const [filter, setFilter] = useState("");
  const [iterations, setIterations] = useState(8);
  const [warmup, setWarmup] = useState(2);
  const [useEtag, setUseEtag] = useState(false);
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<RunSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    setRunning(true);
    setError(null);
    try {
      const data = await apiCall(perf.run, {
        body: {
          filter: filter.trim() || undefined,
          iterations,
          warmup,
          use_etag: useEtag,
        },
      });
      setRun(data);
      refetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  }

  // Column definitions for the per-endpoint results table. DataTable's sortable
  // engine owns the sort state, header chrome, and dividers (no hand-rolled <table>).
  const resultColumns = useMemo<DataTableColumn<PerfResult>[]>(
    () => [
      {
        key: "name",
        header: "Endpoint",
        sort: (a, b) => a.name.localeCompare(b.name),
        render: (r) => (
          <span className="font-mono type-subtext" title={r.url}>
            {r.name}
          </span>
        ),
      },
      {
        key: "p50",
        header: "p50 (ms)",
        defaultDesc: true,
        sort: (a, b) => a.p50 - b.p50,
        render: (r) => fmt(r.p50),
      },
      {
        key: "p95",
        header: "p95 (ms)",
        defaultDesc: true,
        sort: (a, b) => a.p95 - b.p95,
        render: (r) => (
          <span className={r.p95 > 100 ? "text-destructive" : undefined}>
            {fmt(r.p95)}
          </span>
        ),
      },
      {
        key: "max",
        header: "max (ms)",
        defaultDesc: true,
        sort: (a, b) => a.max - b.max,
        render: (r) => fmt(r.max),
      },
      {
        key: "bytes",
        header: "Bytes",
        defaultDesc: true,
        sort: (a, b) => a.bytes - b.bytes,
        render: (r) => (
          <span className="text-muted-foreground">{bytesFmt(r.bytes)}</span>
        ),
      },
      {
        key: "status",
        header: "Status",
        defaultDesc: true,
        sort: (a, b) => a.status - b.status,
        render: (r) =>
          r.ok_rate < 1 ? (
            <Badge variant="destructive">{r.status}</Badge>
          ) : (
            <span className="text-muted-foreground">{r.status}</span>
          ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            {!hideAuditTitle && <CardTitle className="type-title">Audit</CardTitle>}
            <p className={`type-body text-muted-foreground ${hideAuditTitle ? "" : "mt-1"}`}>
              {manifestLoading
                ? "Loading endpoint manifest…"
                : `${manifest?.endpoints.length ?? 0} endpoints across ${manifest?.groups.length ?? 0} groups.`}
            </p>
          </div>
          <Button onClick={handleRun} disabled={running}>
            {running ? "Running…" : "Run benchmark"}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <FormField label="Filter" muted>
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="e.g. crm  or  lens.sessions"
              />
            </FormField>
            <FormField label="Iterations" muted>
              <Input
                type="number"
                min={1}
                max={50}
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value) || 1)}
              />
            </FormField>
            <FormField label="Warmup" muted>
              <Input
                type="number"
                min={0}
                max={20}
                value={warmup}
                onChange={(e) => setWarmup(Number(e.target.value) || 0)}
              />
            </FormField>
            <FormField label="ETag fast-path" muted>
              <div className="flex h-7 items-center gap-2">
                <Switch
                  checked={useEtag}
                  onCheckedChange={setUseEtag}
                  aria-label="ETag fast-path"
                />
                <span className="type-body text-muted-foreground">
                  {useEtag ? "Conditional GET (304s)" : "Fresh fetch"}
                </span>
              </div>
            </FormField>
          </div>
          {error && <p className="type-body text-destructive">Run failed: {error}</p>}
        </CardContent>
      </Card>

      {run && (
        <DataTable<PerfResult>
          title="Results"
          headerAction={
            <div className="flex flex-row items-center gap-3 type-body text-muted-foreground">
              <span>
                p50 <span className="text-foreground">{fmt(run.overall_p50_ms)} ms</span>
              </span>
              <span>
                p95 <span className="text-foreground">{fmt(run.overall_p95_ms)} ms</span>
              </span>
              <Badge variant={run.ok === run.total ? "default" : "destructive"}>
                {run.ok}/{run.total} OK
              </Badge>
              {run.use_etag && <Badge variant="outline">ETag</Badge>}
            </div>
          }
          columns={resultColumns}
          rows={run.results}
          rowKey={(r) => r.name}
          initialSort={{ key: "p95", dir: "desc" }}
        />
      )}

      {history.length > 0 && (
        <DataTable<PerfHistoryEntry>
          title="History"
          subtitle={
            <>
              Persisted runs from <code>docs/perf/history/</code>. Compare p95
              against previous runs to spot regressions.
            </>
          }
          rows={history.slice(0, 20)}
          rowKey={(h) => h.file}
          columns={[
            {
              key: "when",
              header: "When",
              cellClassName: "font-mono type-subtext",
              render: (h) => formatDateTime(h.started_at),
            },
            {
              key: "p50",
              header: "p50",
              render: (h) => fmt(h.overall_p50_ms),
            },
            {
              key: "p95",
              header: "p95",
              render: (h) => fmt(h.overall_p95_ms),
            },
            {
              key: "ok",
              header: "OK",
              render: (h) => `${h.ok}/${h.total}`,
            },
            {
              key: "mode",
              header: "Mode",
              render: (h) =>
                h.use_etag ? (
                  <Badge variant="outline">ETag</Badge>
                ) : (
                  <Badge variant="default">Fresh</Badge>
                ),
            },
          ]}
        />
      )}
    </div>
  );
}
